import { pooledLine } from './buyAFeaturePricing';
import type {
  AggregateResult,
  ParticipantResponse,
  ToolOutputType,
  ToolResultItem,
} from '../types/prioritization/toolSession';

/** Accepts camelCase store rows or raw Supabase snake_case rows. */
type ParticipantResponseLike = {
  isComplete?: boolean;
  is_complete?: boolean;
  results?: ToolResultItem[] | unknown[];
};

/** True when a participant row represents a real submission (not merely is_complete in DB). */
export function isParticipantResponseSubmitted(
  response: ParticipantResponseLike,
  outputType?: ToolOutputType
): boolean {
  const isComplete = Boolean(response.isComplete ?? response.is_complete);
  if (!isComplete) return false;
  const results = response.results;
  if (!Array.isArray(results) || results.length === 0) return false;
  return results.some(item => hasMeaningfulResultItem(item as ToolResultItem, outputType));
}

/** Map a tool_session_responses row (snake or camel) into ParticipantResponse fields analytics need. */
export function normalizeParticipantResponseRow(
  row: Record<string, unknown> | ParticipantResponse,
): Pick<ParticipantResponse, 'participantId' | 'participantName' | 'results' | 'isComplete'> {
  const r = row as Record<string, unknown>;
  const participantId =
    (r.participantId as string | undefined) ??
    (r.participant_id as string | undefined) ??
    '';
  const participantName =
    (r.participantName as string | undefined) ??
    (r.participant_name as string | undefined) ??
    '';
  const results = (r.results as ToolResultItem[] | undefined) ?? [];
  const isComplete = Boolean(r.isComplete ?? r.is_complete);
  return { participantId, participantName, results, isComplete };
}

function classificationCategoryFromItem(item: ToolResultItem): string | undefined {
  const breakdown = item.classificationBreakdown as {
    quadrant?: string;
    moscowCategory?: string;
  } | undefined;
  return item.category ?? breakdown?.quadrant ?? breakdown?.moscowCategory;
}

function hasMeaningfulResultItem(item: ToolResultItem, outputType?: ToolOutputType): boolean {
  if (classificationCategoryFromItem(item)) return true;
  if (outputType === 'classification') {
    return false;
  }
  if (typeof item.score === 'number' && !Number.isNaN(item.score)) return true;
  if (typeof item.rank === 'number' && item.rank >= 1) return true;
  return false;
}

export const QUADRANT_LABELS: Record<string, string> = {
  'quick-wins': 'Quick wins',
  'big-bets': 'Big bets',
  'fill-ins': 'Fill-ins',
  avoid: 'Avoid',
  Must: 'Must have',
  Should: 'Should have',
  Could: 'Could have',
  Wont: "Won't have",
  "Won't": "Won't have",
  // Eisenhower: urgent+important, important, urgent, neither
  ui: 'Do first',
  in: 'Schedule',
  un: 'Delegate',
  nn: 'Eliminate',
  // Pareto
  'vital-few': 'Vital few',
  'useful-many': 'Useful many',
  // Prune the Product Tree
  active: 'Keep',
  pruned: 'Prune',
};

const CLASSIFICATION_CATEGORY_ORDER: Record<string, number> = {
  'quick-wins': 0,
  'big-bets': 1,
  'fill-ins': 2,
  avoid: 3,
  Must: 0,
  Should: 1,
  Could: 2,
  Wont: 3,
  "Won't": 3,
  // Eisenhower: urgent+important, important, urgent, neither
  ui: 0,
  in: 1,
  un: 2,
  nn: 3,
  // Pareto
  'vital-few': 0,
  'useful-many': 1,
  // Prune the Product Tree
  active: 0,
  pruned: 1,
};

/** Where a sorting method's group comes in the result; unknown groups go last. */
export function classificationCategoryOrder(category: string): number {
  return CLASSIFICATION_CATEGORY_ORDER[category] ?? 99;
}

/**
 * The one counting rule, used by the live board and by every Close & save:
 * ranking tools (votes, budget, wins, Borda points) sum; scoring tools use the
 * mean; classification orders by the group most people chose.
 */
export function rankingAggregateScore(agg: AggregateResult): number {
  return agg.totalScore ?? agg.averageScore ?? 0;
}

export function sortAggregatesForDisplay(
  aggregates: AggregateResult[],
  outputType: ToolOutputType
): AggregateResult[] {
  if (outputType === 'classification') {
    // Within a group: the tool's own score when it gives one (Eisenhower,
    // impact/effort, Pareto), then how strongly people agreed on the group.
    return [...aggregates].sort(
      (a, b) =>
        classificationCategoryOrder(a.majorityCategory ?? '') -
          classificationCategoryOrder(b.majorityCategory ?? '') ||
        (b.averageScore ?? 0) - (a.averageScore ?? 0) ||
        (b.consensusStrength ?? 0) - (a.consensusStrength ?? 0)
    );
  }
  if (outputType === 'scoring') {
    return [...aggregates].sort((a, b) => (b.averageScore ?? 0) - (a.averageScore ?? 0));
  }
  return [...aggregates].sort(
    (a, b) => rankingAggregateScore(b) - rankingAggregateScore(a)
  );
}

export function formatAggregateMetric(
  agg: AggregateResult,
  outputType: ToolOutputType,
  /** The method, canonical id — Buy-a-Feature reads as money pooled against a price. */
  toolId?: string | null,
): string {
  if (toolId === 'buy_a_feature') {
    const total = Math.round(agg.totalScore ?? 0);
    const price = agg.participantResults
      .map((pr) => (pr.result?.metadata as { price?: unknown } | undefined)?.price)
      .find((p): p is number => typeof p === 'number');
    return price != null ? pooledLine(total, price) : `$${total}`;
  }
  if (outputType === 'classification') {
    const cat = agg.majorityCategory;
    return cat ? QUADRANT_LABELS[cat] ?? cat : '—';
  }
  if (outputType === 'scoring') {
    return agg.averageScore != null ? `${agg.averageScore} avg` : '—';
  }
  // Pairwise / stack / voting: the summed total (matches Close & save), in the method's own unit.
  const total = agg.totalScore ?? agg.averageScore;
  return total != null ? rankingTotalLabel(Math.round(total), toolId) : '—';
}

/**
 * A ranking method's total in its own unit (roadmap S-38: a member's dot vote
 * read "4 wins"): dots for Dot Voting, wins for Pairwise, points otherwise.
 */
export function rankingTotalLabel(total: number, toolId?: string | null): string {
  if (toolId === 'dot_voting') return `${total} ${total === 1 ? 'dot' : 'dots'}`;
  if (toolId === 'pairwise_ranking') return `${total} ${total === 1 ? 'win' : 'wins'}`;
  return `${total} pts`;
}

export function aggregateBarPercent(
  agg: AggregateResult,
  outputType: ToolOutputType,
  maxScore: number
): number {
  if (outputType === 'classification') {
    return Math.round((agg.consensusStrength ?? 0) * 100);
  }
  if (maxScore <= 0) return 0;
  const value =
    outputType === 'scoring' ? (agg.averageScore ?? 0) : rankingAggregateScore(agg);
  return Math.round((value / maxScore) * 100);
}

/**
 * Sort a single participant's own submitted results for display — by explicit
 * rank when the tool produced one (pairwise, stack ranking, ...), otherwise by
 * score descending (scoring tools), otherwise left in submission order
 * (classification tools group by category instead of ordering).
 */
export function sortMyResultsForDisplay(
  results: ToolResultItem[],
  outputType: ToolOutputType
): ToolResultItem[] {
  if (outputType === 'classification') return [...results];
  if (results.some(item => typeof item.rank === 'number')) {
    return [...results].sort((a, b) => (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER));
  }
  return [...results].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
}

/** Format a single participant's own result item (mirrors formatAggregateMetric but for one person). */
export function formatMyResultMetric(
  item: ToolResultItem,
  outputType: ToolOutputType,
  /** The method, canonical id — the unit a score reads in (dots, wins, dollars). */
  toolId?: string | null,
): string {
  if (toolId === 'buy_a_feature') return item.score != null ? `$${item.score} pledged` : '—';
  if (outputType === 'classification') {
    const cat = classificationCategoryFromItem(item);
    return cat ? QUADRANT_LABELS[cat] ?? cat : '—';
  }
  if (outputType === 'scoring') {
    return item.score != null ? `${item.score} pts` : '—';
  }
  if (item.score != null) return rankingTotalLabel(item.score, toolId);
  return item.rank != null ? `#${item.rank}` : '—';
}

/**
 * "All in, you may reveal" (C07, host console P3): everyone the room expects
 * has submitted. One rule for the host's bar, the host's panel and the Board.
 */
export function roomIsAllIn(submitted: number, expected: number): boolean {
  return expected > 0 && submitted >= expected;
}

